import BaseContainer from 'app/containers/BaseContainer';
import { observer } from 'mobx-react';
import { MainMenu } from './MainMenu';
import { RouteComponentProps, useHistory, useLocation } from 'react-router-dom';
import { useStore } from 'app/context';
import { AdminLogList, AdminLogByDay, AdminLogByMonth, AdminLogByHour, AdminLogNavLeaderboard, AdminLogLoginFailedLeaderboard, AdminLogInfo } from 'app/components/admin/log/AdminLogList';
import { Button } from 'app/components/common/Button';
import { SelectInput, ISelectOpt } from 'app/components/common/Select';
import { useEffect } from 'react';
import { FindSteart } from 'app/models/core/steart.model';

const ViewsDef = [
	{ key: 'log', title: 'Log', view: <AdminLogList /> },
	{ key: 'tag', title: 'Pro Tag', view: <AdminLogByDay /> },
	{ key: 'monat', title: 'Pro Monat', view: <AdminLogByMonth /> },
	{ key: 'stunde', title: 'Pro Stunde', view: <AdminLogByHour /> },
	{ key: 'nav', title: 'Nav Leaderboard', view: <AdminLogNavLeaderboard /> },
	{ key: 'leaderboard', title: 'Login Fehler Leaderboard', view: <AdminLogLoginFailedLeaderboard /> },
	{ key: 'info', title: 'Info', view: <AdminLogInfo /> },
];

const steartOpts = [
	{ value: '', label: 'Alle' },
	...[123, 900, 934, 755, 756, 974, 973, 977, 968, 959, 994, 951, 1001, 1002, 1003].map((id) => ({
		value: String(id),
		label: `${FindSteart(id).name} [${id}]`,
	})),
];

const vdOpts = [
	{ value: '', label: 'Alle' },
	...['B', 'F', 'H', 'K', 'L', 'M', 'N', 'S', 'X'].map((v) => ({ value: v, label: v })),
];

const AdminLogContainer = observer((props: RouteComponentProps) => {
	const { session, adminLogStore } = useStore();
	const currentUser = session.currentUser;
	const history = useHistory();
	const location = useLocation();

	const params = new URLSearchParams(location.search);
	const viewKey = params.get('view') || 'log';
	const filterName = params.get('name') || '';
	const filterSteart = params.get('steart') || '';
	const filterActionType = params.get('actionType') || '';
	const filterAction = params.get('action') || '';
	const filterVd = params.get('vd') || '';
	const filterGs = params.get('gs') || '';
	const filterDateFrom = params.get('dateFrom') || '';
	const filterDateTo = params.get('dateTo') || '';

	const selectedView = ViewsDef.find((d) => d.key === viewKey) || ViewsDef[0];
	const actionOpts = adminLogStore.actionTypes.map((a) => ({ value: a, label: a }));

	useEffect(() => {
		adminLogStore.loadActionTypes();
	}, [adminLogStore]);

	// Sync query params to store filters
	useEffect(() => {
		adminLogStore.setFilters(filterName, filterSteart, filterActionType, filterAction, filterVd, filterGs, filterDateFrom, filterDateTo);
	}, [filterName, filterSteart, filterActionType, filterAction, filterVd, filterGs, filterDateFrom, filterDateTo, adminLogStore]);

	const updateParam = (key: string, value: string) => {
		const p = new URLSearchParams(location.search);
		if (value) {
			p.set(key, value);
		} else {
			p.delete(key);
		}
		history.replace({ search: p.toString() });
	};

	const handleViewChange = (d: typeof ViewsDef[0]) => {
		updateParam('view', d.key);
	};

	const AdminLogNavbar = observer(() => {
		return (
			<div className="admin-navbar">
				<div className="is-left">
					<strong>Log</strong>
				</div>
				<div className="is-right">
					<div className="admin-actions">
						{ViewsDef.map((d) => (
							<Button type="button" key={d.key} className="button is-small" onClick={() => handleViewChange(d)}>
								{d.title}
							</Button>
						))}
					</div>
				</div>
			</div>
		);
	});

	return (
		<BaseContainer classModifier="admin-container admin-users-container" sideMenu={<MainMenu {...props} />} navbar={<AdminLogNavbar />}>
			{currentUser && (
				<>
					<div className="admin-filters" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
						<label>
							Name/Bensl/BnrId:
							<input
								type="text"
								className="input is-small"
								style={{ marginLeft: '0.5rem', width: '200px' }}
								value={filterName}
								onChange={(e) => updateParam('name', e.target.value)}
								placeholder="Suche..."
							/>
						</label>
						<label>
							Steart:
							<select
								className="input is-small"
								style={{ marginLeft: '0.5rem' }}
								value={filterSteart}
								onChange={(e) => updateParam('steart', e.target.value)}
							>
								{steartOpts.map((o) => (
									<option key={o.value} value={o.value}>{o.label}</option>
								))}
							</select>
						</label>
						<div style={{ minWidth: '200px' }}>
							<SelectInput
								name="actionType"
								label="Action Type"
								isMulti={true}
								emptyText="Alle"
								isClearable={true}
								value={filterActionType ? filterActionType.split(',') : []}
								opts={actionOpts}
								onChange={(_name: string, selected: ISelectOpt[] | null) => {
									const values = (selected || []).map((o) => o.value).join(',');
									updateParam('actionType', values);
								}}
							/>
						</div>
						<label>
							Action:
							<input
								type="text"
								className="input is-small"
								style={{ marginLeft: '0.5rem', width: '200px' }}
								value={filterAction}
								onChange={(e) => updateParam('action', e.target.value)}
								placeholder="Suche..."
							/>
						</label>
						<label>
							VD:
							<select
								className="input is-small"
								style={{ marginLeft: '0.5rem' }}
								value={filterVd}
								onChange={(e) => updateParam('vd', e.target.value)}
							>
								{vdOpts.map((o) => (
									<option key={o.value} value={o.value}>{o.label}</option>
								))}
							</select>
						</label>
						<label>
							GS:
							<input
								type="text"
								className="input is-small"
								style={{ marginLeft: '0.5rem', width: '80px' }}
								value={filterGs}
								onChange={(e) => updateParam('gs', e.target.value)}
								placeholder="z.B. 001"
							/>
						</label>
						<label>
							Von:
							<input
								type="datetime-local"
								className="input is-small"
								style={{ marginLeft: '0.5rem' }}
								value={filterDateFrom}
								onChange={(e) => updateParam('dateFrom', e.target.value)}
							/>
						</label>
						<label>
							Bis:
							<input
								type="datetime-local"
								className="input is-small"
								style={{ marginLeft: '0.5rem' }}
								value={filterDateTo}
								onChange={(e) => updateParam('dateTo', e.target.value)}
							/>
						</label>
					</div>
					<strong>{selectedView.title}</strong>
					<div className="table-container">{selectedView.view}</div>
				</>
			)}
		</BaseContainer>
	);
});

export default AdminLogContainer;
